// Sync button with progress indicator. Triggers POST /api/sync and polls
// GET /api/sync/status every 5 seconds while running.

import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type SyncStatus } from '../lib/api';

const POLL_INTERVAL = 5000;

const PHASE_LABELS: Record<string, string> = {
  'fetch-accounts': 'Fetching accounts',
  'geocode': 'Geocoding addresses',
  'fetch-views': 'Fetching views',
  'fetch-view-members': 'Loading view members',
  'generate-geojson': 'Generating map data',
  'done': 'Complete',
};

export default function SyncButton() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Poll sync status
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;

    async function poll() {
      try {
        const s = await api.getSyncStatus();
        setStatus(s);
        if (s.status === 'running') {
          timer = setInterval(async () => {
            try {
              const updated = await api.getSyncStatus();
              setStatus(updated);
              if (updated.status !== 'running') {
                clearInterval(timer);
                // Invalidate view cache so map reloads
                queryClient.invalidateQueries({ queryKey: ['views'] });
              }
            } catch {
              // ignore poll errors
            }
          }, POLL_INTERVAL);
        }
      } catch {
        // ignore initial poll error
      }
    }

    poll();
    return () => clearInterval(timer);
  }, [starting, queryClient]);

  async function handleSync() {
    setError(null);
    setStarting(true);
    try {
      await api.triggerSync();
      // Start polling
      const s = await api.getSyncStatus();
      setStatus(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start sync');
    } finally {
      setStarting(false);
    }
  }

  const isRunning = status?.status === 'running';
  const progress = status?.progress;

  function getProgressPercent(): number {
    if (!progress) return 0;
    const phase = status?.phase ?? '';
    if (phase === 'fetch-accounts' && progress.totalAccounts > 0) {
      return Math.round((progress.fetchedAccounts / progress.totalAccounts) * 30);
    }
    if (phase === 'geocode' && progress.totalAccounts > 0) {
      return 30 + Math.round((progress.geocodedAccounts / progress.totalAccounts) * 30);
    }
    if (phase === 'fetch-views') return 65;
    if (phase === 'fetch-view-members' && progress.totalViews > 0) {
      return 65 + Math.round((progress.processedViews / progress.totalViews) * 20);
    }
    if (phase === 'generate-geojson') return 90;
    if (phase === 'done') return 100;
    return 0;
  }

  return (
    <div className="flex items-center gap-3">
      <button
        onClick={handleSync}
        disabled={isRunning || starting}
        className="bg-brand-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {starting ? 'Starting...' : isRunning ? 'Syncing...' : 'Sync'}
      </button>

      {isRunning && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <div className="h-2 w-32 bg-gray-200">
            <div
              className="h-full bg-brand-500 transition-all duration-500"
              style={{ width: `${getProgressPercent()}%` }}
            />
          </div>
          <span className="whitespace-nowrap">
            {PHASE_LABELS[status?.phase ?? ''] ?? status?.phase}
          </span>
        </div>
      )}

      {status?.status === 'error' && (
        <span className="text-sm text-red-600">Error: {status.error}</span>
      )}

      {error && <span className="text-sm text-red-600">{error}</span>}

      {status?.completedAt && status?.status === 'idle' && (
        <span className="text-xs text-gray-400">
          Last synced: {new Date(status.completedAt).toLocaleString()}
        </span>
      )}
    </div>
  );
}
